import React, { useState } from 'react'
import { useSelector } from 'react-redux'
import { useHasAnonymousError } from '../../FormView/FormView'
import Details, {DetailsItem} from '../../Details/Details'
import Remarks from '../../Remarks/Remarks'
import Dropdown from '../../Dropdown/Dropdown'

import Edit from '@mui/icons-material/Edit'

import GenerateInstallationFields from './GenerateInstallationFields'

import { TextField, Button } from '@mui/material';

function Installation(props, {errorElements = []}) {
	const [state, setState] = useState({
		allowDangerousEdits: false,
	})
	const { t } = useSelector(redux => redux.store)
	const hasAnonymousError = useHasAnonymousError(errorElements)

	function filterInstallationData(data, installationFields, additionalFields, ignoreFields) {
		let installationData = {}
		let additionalData = {}
		let technicalData = {}

		Object.keys(data).map(item => {
			if (installationFields.indexOf(item) > -1) {
				return installationData[item] = data[item]
			} else if (additionalFields.indexOf(item) > -1) {
				return additionalData[item] = data[item]
			} else if (ignoreFields.indexOf(item) > -1) {
				return ''
			} else {
				return technicalData[item] = data[item]
			}
		})

		return { installationData, additionalData, technicalData }
	}

	function setReadOnlyProp(data, readOnlyFields) {
		Object.keys(data).map(item => {
			if (readOnlyFields.indexOf(item) > -1) {
				return data[item].readOnly = true
			} else {
				return ''
			}
		})
	}

	function allowDangerousEdits() {
		if (window.confirm('Weet je zeker dat je dit veld wil wijzigen?')) {
			setState({ allowDangerousEdits: true })
		}
	}

	setReadOnlyProp(props.data, [
		'installation_status_name',
		'installation_start_date',
		'installation_end_date',
		'employee_fullname',
		'installation_epm_name',
		'installation_in_contract',
	])

	const installationFields = [
		'building_name',
		'installation_contract_price',
		'installation_type_name',
		'installation_placement_address',
		'installation_zipcode',
		'installation_indication',
		'installation_code_number',
		'installation_eq_number',
		'installation_notified_body_number',
		'installation_manufacture',
		'installation_manufacture_number',
		'installation_seller',
		'installation_build_year',
		'installation_start_use_year',
		'installation_contract_maintenance',
		'installation_renter',
		'customer_name', // Installateur
		'other_customer_name', // Vaste keurende instantie
		'installation_testing_status_name',
		'installation_location_name',
		'installation_usage_name',
		'installation_vandalism_name',
		'remark_text',
		'installation_safety_norm_name'
	]

	const additionalFields = [
		'installation_status_name',
		'installation_start_date',
		'installation_end_date',
		'installation_inspect',
		'employee_fullname', // Vaste inspecteur
		'installation_inspection_interval',
		'installation_epm_name',
		'installation_out_of_use',
		'installation_in_contract',
		'installation_lessened_use',
		'installation_lessened_use_date',
		'installation_start_maintenance',
		'installation_seen_testing_result',
		'installation_seen_testing_executed',
	]

	const ignoreFields = [
		'installation_invoice_to',
		'installation_photo',
	]

	const { installationData, additionalData, technicalData } = filterInstallationData(props.data, installationFields, additionalFields, ignoreFields)

	function notRequired(id) {
		return !installationData[id].required || null
	}

	const renderDetailsItem = (id, child) => {
		return (
			<DetailsItem
				label={installationData[id].label}
				required={installationData[id].required}
				type={installationData[id].type}
				error={hasAnonymousError(id)}>
				{child}
			</DetailsItem>
		)
	}

	const renderTextField = (id, disabled) => {
		return renderDetailsItem(id,
			<TextField
				variant="standard"
				id={id}
				name={id}
				defaultValue={installationData[id].value}
				inputProps={{'data-notrequired': disabled || notRequired(id) }}
				error={hasAnonymousError(id)}
				fullWidth
				disabled={disabled} />
		);
	}

	const renderNumberField = (id) => {
		return renderDetailsItem(id,
			<TextField
				variant="standard"
				id={id}
				name={id}
				defaultValue={installationData[id].value}
				inputProps={{'data-notrequired': notRequired(id) }}
				error={hasAnonymousError(id)}
				fullWidth
				type="number" />
		);
	}

	const renderDropdown = id => {
		return renderDetailsItem(id,
			<Dropdown
				id={id}
				name={id}
				value={installationData[id].value}
				options={installationData[id].options}
				data-notrequired={ notRequired(id) }
				error={hasAnonymousError(id)}
				fullWidth
			/>
		)
	}

	return (
		<div className="installatie">
			<div className="grid-inline grid-inline-start">
				<div className="grid-1-2">
					<Details header={installationData.building_name.label}>
						<DetailsItem label={installationData.building_name.label}>
							{installationData.building_name.text} {/* NOTE: .text, because .value is a FK */}
						</DetailsItem>
						<DetailsItem label={installationData.installation_placement_address.label}>
							{/*
								Placement address is a required field by remote API, so even if it is missing
								from the installation details, it needs to be sent. As a workaround, the field’s
								value is always sent with the form as a hidden field.
							 */}
							<input type="hidden" name="installation_placement_address" value={installationData.installation_placement_address.value}/>
							{installationData.installation_placement_address.value}
						</DetailsItem>
						<DetailsItem label={installationData.installation_zipcode.label}>
							{installationData.installation_zipcode.value}
						</DetailsItem>
					</Details>
				</div>
				<div className="grid-1-2">
					<Details header={t.installation_kind}>
						<DetailsItem label={installationData.installation_type_name.label}>
							{installationData.installation_type_name.text} {/* NOTE: .text, because .value is a FK */}
						</DetailsItem>
						<DetailsItem label={installationData.installation_indication.label}>
							{installationData.installation_indication.value}
						</DetailsItem>
						<DetailsItem label={installationData.installation_code_number.label}>
							{installationData.installation_code_number.value}
						</DetailsItem>
						{/*
							If "dangerous edits" are allowed — state is set by clicking the
							Bewerken button below — the field is rendered as a text field;
							otherwise, as a <DetailsItem /> component. To compensate for
							layout differences, ugly margin hacks be plenty.
						*/}
						{ state.allowDangerousEdits ?
							renderTextField('installation_eq_number')
							:
							<DetailsItem label={installationData.installation_eq_number.label}>
								<div style={{ marginTop: 11 }}>
									{installationData.installation_eq_number.value}
									<Button
										color="inherit"
										variant="contained"
										style={{ marginTop: -4, marginLeft: 8 }}
										size="small"
										startIcon={<Edit />}
										onClick={() => allowDangerousEdits()}
									>
										{t.edit}
									</Button>
								</div>
							</DetailsItem>
						}
					</Details>
				</div>
			</div>
			<div className="grid-inline grid-inline-start">
				<div className="grid-1-1">
					<Details header={t.inpection_instal_notes}>
						<Remarks data-notrequired
							name="remark_text"
							remarks={props.remark_text}
							endpoint={`installations/${props.installation_id}/remarks`}
						/>
					</Details>
				</div>
			</div>
			<div className="grid-inline grid-inline-start spacing-default-top">
				<div className="grid-1-2">
					<Details header={t.inpection_base_information}>
						{renderTextField('installation_contract_price', true)}
						{renderTextField('installation_notified_body_number')}
						{renderDropdown('other_customer_name')}
						{renderDropdown('installation_testing_status_name')}
						{renderTextField('installation_indication')}
						{renderTextField('installation_manufacture')}
						{renderTextField('installation_manufacture_number')}
						{renderTextField('installation_seller')}
						{renderNumberField('installation_build_year')}
					</Details>
				</div>
				<div className="grid-1-2">
					<Details header="&nbsp;">
						{renderNumberField('installation_start_use_year')}
						{renderNumberField('installation_contract_maintenance')}
						{renderTextField('installation_renter')}
						{renderDropdown('customer_name')}
						{renderDropdown('installation_location_name')}
						{renderDropdown('installation_usage_name')}
						{renderDropdown('installation_vandalism_name')}
						{renderDropdown('installation_safety_norm_name')}
					</Details>
				</div>
			</div>
			<div className="spacing-default-top spacing-default-bottom">
				<GenerateInstallationFields
					data={additionalData}
					header={t.inpection_additional_information}
					error={hasAnonymousError()}/>
			</div>
			<div className="spacing-default-top spacing-default-bottom">
				<GenerateInstallationFields
					data={technicalData}
					header={t.inpection_technical_information}
					error={hasAnonymousError()}/>
			</div>
		</div>
	)
}

export default Installation
